"use client";

import { Button } from "../../";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../";
import { cn } from "../../utils";
import type { FileUIPart, SourceDocumentUIPart } from "ai";
import {
  IconFile,
  IconWorld,
  IconPhoto,
  IconMusic,
  IconPaperclip,
  IconVideo,
  IconX,
} from "@tabler/icons-react";
import type { ComponentProps, HTMLAttributes, ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import { Stack } from "../../components/stack/Stack";

// ============================================================================
// Types
// ============================================================================

export type AttachmentData =
  | (FileUIPart & { id: string })
  | (SourceDocumentUIPart & { id: string });

export type AttachmentMediaCategory =
  | "image"
  | "video"
  | "audio"
  | "document"
  | "source"
  | "unknown";

export type AttachmentVariant = "grid" | "inline" | "list";

// ============================================================================
// Utility Functions
// ============================================================================

export const getMediaCategory = (
  data: AttachmentData,
): AttachmentMediaCategory => {
  if (data.type === "source-document") {
    return "source";
  }

  const mediaType = data.mediaType ?? "";

  if (mediaType.startsWith("image/")) {
    return "image";
  }
  if (mediaType.startsWith("video/")) {
    return "video";
  }
  if (mediaType.startsWith("audio/")) {
    return "audio";
  }
  if (mediaType.startsWith("application/") || mediaType.startsWith("text/")) {
    return "document";
  }

  return "unknown";
};

export const getAttachmentLabel = (data: AttachmentData): string => {
  if (data.type === "source-document") {
    return data.title || data.filename || "Source";
  }

  const category = getMediaCategory(data);
  return data.filename || (category === "image" ? "Image" : "Attachment");
};

// ============================================================================
// Contexts
// ============================================================================

interface AttachmentsContextValue {
  variant: AttachmentVariant;
}

export const AttachmentsContext = createContext<AttachmentsContextValue | null>(
  null,
);

interface AttachmentContextValue {
  data: AttachmentData;
  mediaCategory: AttachmentMediaCategory;
  onRemove?: () => void;
  variant: AttachmentVariant;
}

const AttachmentContext = createContext<AttachmentContextValue | null>(null);

// ============================================================================
// Hooks
// ============================================================================

export const useAttachmentsContext = () =>
  useContext(AttachmentsContext) ?? { variant: "grid" as const };

export const useAttachmentContext = () => {
  const ctx = useContext(AttachmentContext);
  if (!ctx) {
    throw new Error("Attachment components must be used within <Attachment>");
  }
  return ctx;
};

// ============================================================================
// Attachments - Container
// ============================================================================

export type AttachmentsProps = HTMLAttributes<HTMLDivElement> & {
  variant?: AttachmentVariant;
};

export const Attachments = ({
  variant = "grid",
  className,
  children,
  ...props
}: AttachmentsProps) => {
  const contextValue = useMemo(() => ({ variant }), [variant]);

  return (
    <AttachmentsContext.Provider value={contextValue}>
      <div
        className={cn(
          "al-flex al-items-start",
          variant === "list" ? "al-flex-col al-gap-2" : "al-flex-wrap al-gap-2",
          variant === "grid" && "al-ml-auto al-w-fit",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentsContext.Provider>
  );
};

// ============================================================================
// Attachment - Item
// ============================================================================

export type AttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: AttachmentData;
  onRemove?: () => void;
};

export const Attachment = ({
  data,
  onRemove,
  className,
  children,
  ...props
}: AttachmentProps) => {
  const { variant } = useAttachmentsContext();
  const mediaCategory = getMediaCategory(data);

  const contextValue = useMemo<AttachmentContextValue>(
    () => ({ data, mediaCategory, onRemove, variant }),
    [data, mediaCategory, onRemove, variant],
  );

  return (
    <AttachmentContext.Provider value={contextValue}>
      <div
        className={cn(
          "al-group al-relative",
          "al-border-none",
          variant === "grid" && "al-size-24 al-overflow-hidden al-rounded-lg",
          variant === "inline" && [
            "al-flex al-h-8 al-cursor-pointer al-select-none al-items-center al-gap-1.5",
            "al-rounded-md al-border al-border-border al-px-1.5",
            "al-font-medium al-text-sm al-transition-all",
            "hover:al-bg-accent hover:al-text-accent-foreground dark:hover:al-bg-accent/50",
          ],
          variant === "list" && [
            "al-flex al-w-full al-items-center al-gap-3 al-rounded-lg al-border p-3",
            "hover:al-bg-accent/50",
          ],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentContext.Provider>
  );
};

// ============================================================================
// AttachmentPreview - Media preview
// ============================================================================

export type AttachmentPreviewProps = HTMLAttributes<HTMLDivElement> & {
  fallbackIcon?: ReactNode;
};

export const AttachmentPreview = ({
  fallbackIcon,
  className,
  ...props
}: AttachmentPreviewProps) => {
  const { data, mediaCategory, variant } = useAttachmentContext();

  const iconSize = variant === "inline" ? "size-3" : "size-4";

  const renderIcon = (Icon: typeof IconPhoto) => (
    <Icon className={cn(iconSize, "al-text-primary")} />
  );

  const getIcon = () => {
    const iconMap: Record<AttachmentMediaCategory, typeof IconPhoto> = {
      image: IconPhoto,
      video: IconVideo,
      audio: IconMusic,
      source: IconWorld,
      document: IconFile,
      unknown: IconPaperclip,
    };

    const Icon = iconMap[mediaCategory];
    return fallbackIcon ?? renderIcon(Icon);
  };

  const renderContent = () => {
    return (
      <Stack className="al-text-nowrap al-max-w-full" align="center">
        {getIcon()} <div className="al-flex-1 al-truncate">{data.filename}</div>
      </Stack>
    );
  };

  return (
    <div
      className={cn(
        "al-flex al-shrink-0 al-items-center al-justify-center al-overflow-hidden",
        variant === "grid" && "al-size-full al-bg-muted",
        variant === "inline" &&
          "al-max-size-12 al-rounded al-max-w-full al-flex-1",
        variant === "list" && "al-size-12 al-rounded al-bg-muted",
        className,
      )}
      {...props}
    >
      {renderContent()}
    </div>
  );
};

// ============================================================================
// AttachmentInfo - Name and type display
// ============================================================================

export type AttachmentInfoProps = HTMLAttributes<HTMLDivElement> & {
  showMediaType?: boolean;
};

export const AttachmentInfo = ({
  showMediaType = false,
  className,
  ...props
}: AttachmentInfoProps) => {
  const { data, variant } = useAttachmentContext();
  const label = getAttachmentLabel(data);

  if (variant === "grid") {
    return null;
  }

  return (
    <div className={cn("al-min-w-0 al-flex-1", className)} {...props}>
      <span className="al-block al-truncate">{label}</span>
      {showMediaType && data.mediaType && (
        <span className="al-block al-truncate al-text-muted-foreground al-text-xs">
          {data.mediaType}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// AttachmentRemove - Remove button
// ============================================================================

export type AttachmentRemoveProps = ComponentProps<typeof Button> & {
  label?: string;
};

export const AttachmentRemove = ({
  label = "Remove",
  className,
  children,
  ...props
}: AttachmentRemoveProps) => {
  const { onRemove, variant } = useAttachmentContext();

  if (!onRemove) {
    return null;
  }

  return (
    <Button
      aria-label={label}
      className={cn(
        variant === "grid" && [
          "al-absolute al-top-2 al-right-2 al-size-6 al-rounded-full al-p-0",
          "al-bg-background/80 al-backdrop-blur-sm",
          "al-opacity-0 al-transition-opacity group-hover:al-opacity-100",
          "hover:al-bg-background",
          "[&>svg]:al-size-3",
        ],
        variant === "inline" && [
          "al-size-3 al-rounded al-p-0 !al-bg-transparent",
          "[&>svg]:al-size-2.5",
        ],
        variant === "list" && [
          "al-size-8 al-shrink-0 al-rounded p-0",
          "[&>svg]:al-size-4",
        ],
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <IconX />}
      <span className="al-sr-only">{label}</span>
    </Button>
  );
};

// ============================================================================
// AttachmentHoverCard - Hover preview
// ============================================================================

export type AttachmentHoverCardProps = ComponentProps<typeof HoverCard>;

export const AttachmentHoverCard = ({
  openDelay = 0,
  closeDelay = 0,
  ...props
}: AttachmentHoverCardProps) => (
  <HoverCard closeDelay={closeDelay} openDelay={openDelay} {...props} />
);

export type AttachmentHoverCardTriggerProps = ComponentProps<
  typeof HoverCardTrigger
>;

export const AttachmentHoverCardTrigger = (
  props: AttachmentHoverCardTriggerProps,
) => <HoverCardTrigger {...props} />;

export type AttachmentHoverCardContentProps = ComponentProps<
  typeof HoverCardContent
>;

export const AttachmentHoverCardContent = ({
  align = "start",
  className,
  ...props
}: AttachmentHoverCardContentProps) => (
  <HoverCardContent
    align={align}
    className={cn("al-w-auto al-p-2", className)}
    {...props}
  />
);

// ============================================================================
// AttachmentEmpty - Empty state
// ============================================================================

export type AttachmentEmptyProps = HTMLAttributes<HTMLDivElement>;

export const AttachmentEmpty = ({
  className,
  children,
  ...props
}: AttachmentEmptyProps) => (
  <div
    className={cn(
      "al-flex al-items-center al-justify-center al-p-4 al-text-muted-foreground al-text-sm",
      className,
    )}
    {...props}
  >
    {children ?? "No attachments"}
  </div>
);
